// ============================================================================
// /people/hr/directory/:employeeId — the employee card.
//
// Six sections, in the order Antonela's mockup put them: Employment,
// Compensation, Benefits (status only), Placement, Identity & contact,
// Emergency contact. Everything on it is editable, but not all through one
// button: demographic fields go through EmployeeEditModal (one UPDATE on
// employees), while compensation and benefit elections go through the audited
// RPCs, because those two are HISTORIES. Overwriting a wage rate in place would
// destroy the fact that it used to be something else, which is the one thing
// payroll and an unemployment claim both need.
//
// Deliberately NOT on this card, and each for a reason:
//   · a compensation-history list — it is loaded and shown, because the demo's
//     "no history anywhere" was a sketch artefact, not a requirement, and a
//     raise you cannot see is indistinguishable from a typo.
//   · a file-upload photo affordance. photo_url renders when set, but there is
//     no storage bucket for headshots yet, so a camera button here would be a
//     button that cannot work. Flagged, not faked.
// ============================================================================
import { canWriteHrRecord, seesFullDateOfBirth } from '../../shared/role-gates';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft, Pencil, UserMinus, RotateCcw, Eye, EyeOff, AlertTriangle,
  Briefcase, DollarSign, ShieldCheck, MapPin, User, Phone, History,
  ArrowRightLeft, FileText,
} from 'lucide-react';
import {
  PageHeader, Card, ErrorState, Skeleton, Button, Avatar, useToast,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';
import {
  useEmployeeDetail,
  useEmployeeBenefits,
  useCompensationHistory,
  updateEmployee,
  EMPLOYEE_FORMER_STATUS,
} from './hooks/use-hr-data';
import EmployeeEditModal from './employee-edit-modal';
import ConvertToClientModal from './convert-to-client-modal';
import CompensationModal from './compensation-modal';
import BenefitElectionModal from './benefit-election-modal';
import PayTypeModal from './pay-type-modal';
import { fmtMonthDay, fmtMonthDayYear, yearsSince } from './utils';
import { formatPayRate, formatStandardHours, displayName } from './people-directory';
import { errorMessage } from '../../shared/errors';

const DASH = '—';

type Modal = null | 'edit' | 'comp' | 'benefit' | 'paytype' | 'convert';

// ⛔ The RPC no longer enumerates statuses at all — it refuses anything that is
// not 'Active', or that already carries a separation_id. Enumerating was the
// bug: 'Archived' was missing from the old list, so all three archived
// employees could be converted a second time. This list is the button's mirror
// of the same rule and stays wide only so a stale row still reads correctly.
const SEPARATED_STATUSES: ReadonlyArray<string> = [
  'Former',
  'Separated',
  'Terminated',
  'Archived',
];

export default function HrEmployeeCard() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { data, loading, error, refetch } = useEmployeeDetail(employeeId);
  const benefits = useEmployeeBenefits(employeeId);
  const history = useCompensationHistory(employeeId);
  const { profile } = useAuth();
  const toast = useToast();
  const [showFullDob, setShowFullDob] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [working, setWorking] = useState(false);

  const canShowFullDob = seesFullDateOfBirth(profile);
  const canWrite = canWriteHrRecord(profile);

  function refreshAll() {
    refetch();
    history.refetch();
    benefits.refetch();
  }

  async function setStatus(next: string, label: string) {
    if (!employeeId) return;
    setWorking(true);
    try {
      await updateEmployee(employeeId, { employment_status: next });
      toast.success(label);
      refetch();
    } catch (err) {
      toast.error('Could not update', { description: errorMessage(err, 'Permission denied.') });
    } finally {
      setWorking(false);
    }
  }

  if (error) return <ErrorState title="Couldn't load employee" body={error} />;
  if (loading || !data) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <Skeleton className="h-8 w-48" />
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const e = data as Record<string, string | number | null>;
  const s = (k: string): string | null => {
    const v = e[k];
    return v === null || v === undefined || v === '' ? null : String(v);
  };
  const name = displayName({
    employee_name_first: s('employee_name_first'),
    employee_name_last: s('employee_name_last'),
    employee_id: String(e.employee_id),
  });
  const payType = s('pay_type');
  const status = s('employment_status') ?? 'Active';
  const isFormer = status === EMPLOYEE_FORMER_STATUS;
  const sepId = s('separation_id');
  const dob = s('dob');
  const hire = s('hire_date');
  const yrs = yearsSince(hire);
  const current = history.data[0] ?? null;
  const photo = s('photo_url');

  // The card's own worklist. Pay type is what the directory groups on, so an
  // unset value is not cosmetic — the person is filed under "Pay type not set"
  // and nothing can say whether they are benefits-eligible.
  const needsPayType = !payType;

  // Conversion = an hourly TCS worker is hired DIRECTLY by the client and comes
  // off TCS payroll. Ben, 2026-08-25: "conversions are only for hourly employees
  // without a TCS email address."
  //
  // ⛔ That rule is expressed as `pay_type = 'Hourly'` and NOT as an email test.
  // The retired employee-detail.tsx gated on
  // `!email_work.includes('terminalcleaning.com')`, which was narrower than the
  // server (the RPC checks only W2 + not-separated) and wrong in both directions:
  // an hourly worker who is later given a TCS email would be refused a
  // conversion they qualify for, and the test says nothing at all about someone
  // with no email on file. Measured 2026-08-25: all 5 `pay_type='Hourly'`
  // employees have no TCS email and every TCS-email holder is Salaried, so
  // `pay_type` expresses Ben's rule exactly and keeps expressing it when
  // somebody's email changes. That is what the column was added for.
  const isSeparated = SEPARATED_STATUSES.includes(status);
  const canConvert =
    canWrite &&
    payType === 'Hourly' &&
    s('employment_type') === 'W2' &&
    !isSeparated &&
    !s('separation_id');

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6">
      <Link
        to="/people/hr/directory"
        className="inline-flex items-center gap-1 text-xs text-tcs-text-secondary hover:underline"
      >
        <ChevronLeft className="h-3 w-3" /> People
      </Link>

      <PageHeader
        eyebrow="HR › Employee"
        title={name}
        meta={[s('employee_title'), String(e.employee_id), s('employment_type'), payType]
          .filter(Boolean)
          .join(' · ')}
        actions={
          canWrite ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                icon={<Pencil className="h-3.5 w-3.5" />}
                onClick={() => setModal('edit')}
              >
                Edit
              </Button>
              {canConvert && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<ArrowRightLeft className="h-3.5 w-3.5" />}
                  onClick={() => setModal('convert')}
                >
                  Convert to client hire
                </Button>
              )}
              {/* ⛔ "Archive" is GONE (2026-08-26). It wrote employment_status
                  = 'Archived', a status that meant exactly the same thing as
                  Former and that no rule distinguished — three of the seven
                  off-roster people had no separation date and no reason under
                  it. Flipping a status was also the ONLY way anybody was ever
                  offboarded here, which is why employee_separations sat at zero
                  rows while seven people had walked out still holding cards,
                  facilities and app logins. Recording the separation is the
                  replacement: same intent, one screen further, and it produces
                  a checklist somebody owns. */}
              {isFormer ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={working}
                  icon={<RotateCcw className="h-3.5 w-3.5" />}
                  onClick={() => setStatus('Active', 'Employee restored')}
                >
                  Restore
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<UserMinus className="h-3.5 w-3.5" />}
                  as={Link}
                  to="/people/separations/new"
                >
                  Record a separation
                </Button>
              )}
            </>
          ) : undefined
        }
      />

      {needsPayType && (
        <Banner tone="warn">
          No pay type is set, so this person is filed under <strong>Pay type not set</strong> in the
          directory and nothing can say whether they are benefits-eligible.
          {canWrite && (
            <button
              type="button"
              onClick={() => setModal('paytype')}
              className="ml-1 underline"
            >
              Set it now.
            </button>
          )}
        </Banner>
      )}

      {isFormer && (
        <Banner tone="warn">
          This employee is former and appears in no picker anywhere in the app. They still
          resolve by name on the facilities, tasks and audit rows that reference them.
          {sepId ? (
            <>
              {' '}
              <Link to={`/people/separations/${sepId}`} className="underline">
                Open {sepId}
              </Link>
              .
            </>
          ) : (
            <> No separation record exists for them — that is what the Loose Ends report chases.</>
          )}
        </Banner>
      )}

      {/* Head: photo tile, name, status, quick actions */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-4">
          {photo ? (
            <img
              src={photo}
              alt={name}
              className="h-20 w-20 rounded-xl border border-tcs-border object-cover"
            />
          ) : (
            <Avatar name={name} size="lg" className="!h-20 !w-20 !rounded-xl !text-2xl" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xl font-semibold text-tcs-text">{name}</span>
              <StatusChip status={status} />
            </div>
            <div className="mt-0.5 text-xs text-tcs-text-muted">
              {[s('employee_title'), String(e.employee_id), s('employment_type'), payType]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        </div>

        {canWrite && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-tcs-border pt-4">
            <Button variant="secondary" size="sm" onClick={() => setModal('comp')}>
              Change compensation
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setModal('paytype')}>
              Set pay type &amp; work state
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setModal('benefit')}>
              Update benefits status
            </Button>
          </div>
        )}
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Section icon={<Briefcase className="h-4 w-4" />} title="Employment">
          <Row label="Employment type" value={s('employment_type') ?? DASH} />
          <Row label="Pay type" value={payType ?? DASH} />
          <Row label="Role type" value={s('role_type') ?? DASH} />
          <Row label="Status" value={status} />
          <Row label="Hire date" value={hire ? fmtMonthDayYear(hire) : DASH} />
          <Row
            label="Anniversary"
            value={yrs === null ? DASH : `${yrs} year${yrs === 1 ? '' : 's'}`}
          />
          <Row
            label="End date"
            value={s('separation_date') ? fmtMonthDayYear(s('separation_date')) : DASH}
          />
        </Section>

        <Section icon={<DollarSign className="h-4 w-4" />} title="Compensation">
          <Row label="Wage rate" value={formatPayRate(current, payType)} />
          <Row label="Pay frequency" value={current?.pay_frequency ?? DASH} />
          <Row label="Standard hours" value={formatStandardHours(current)} />
          <Row
            label="Effective"
            value={current?.effective_date ? fmtMonthDayYear(current.effective_date) : DASH}
          />
        </Section>

        <BenefitsSection
          payType={payType}
          rows={benefits.data}
          loading={benefits.loading}
        />

        <Section icon={<MapPin className="h-4 w-4" />} title="Placement">
          <Row label="Location" value={s('location') ?? DASH} />
          <Row label="Primary facility" value={s('primary_facility_id') ?? DASH} />
          <Row
            label="Work state"
            value={
              s('work_state')
                ? `${s('work_state')}${
                    s('work_state_source') ? ` (${s('work_state_source')?.replace(/_/g, ' ')})` : ''
                  }`
                : DASH
            }
          />
          <Row label="Current shift" value={s('current_shift') ?? DASH} />
          <Row label="App role" value={s('app_role') ?? DASH} />
        </Section>

        <Section icon={<User className="h-4 w-4" />} title="Identity & contact">
          <Row
            label="Birthday"
            value={
              <span className="flex items-center gap-2">
                <span>
                  {dob ? (showFullDob && canShowFullDob ? fmtMonthDayYear(dob) : fmtMonthDay(dob)) : DASH}
                </span>
                {dob && canShowFullDob && (
                  <button
                    type="button"
                    onClick={() => setShowFullDob((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs text-tcs-text-secondary hover:underline"
                  >
                    {showFullDob ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {showFullDob ? 'Hide year' : 'Show full DOB'}
                  </button>
                )}
                {dob && !canShowFullDob && (
                  <span className="text-xs text-tcs-text-muted">year hidden</span>
                )}
              </span>
            }
          />
          <Row label="Work email" value={s('email_work') ?? DASH} />
          <Row label="Personal email" value={s('email_personal') ?? DASH} />
          <Row label="Phone" value={s('phone') ?? DASH} />
        </Section>

        <Section icon={<Phone className="h-4 w-4" />} title="Emergency contact">
          <Row label="Name" value={s('emergency_contact_name') ?? DASH} />
          <Row label="Relationship" value={s('emergency_contact_relationship') ?? DASH} />
          <Row label="Phone" value={s('emergency_contact_phone') ?? DASH} />
        </Section>
      </div>

      {/* Notes. Carried over from the retired employee-detail.tsx, which was the
          only surface that displayed them — deleting that page without this
          would have made real HR notes invisible while still editable. */}
      {s('notes') && (
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
            <FileText className="h-4 w-4" />
            <span>Notes</span>
          </div>
          <div className="mt-2 whitespace-pre-wrap text-sm text-tcs-text">{s('notes')}</div>
        </Card>
      )}

      {history.data.length > 1 && (
        <Card className="overflow-hidden p-0">
          <div className="flex items-center gap-2 border-b border-tcs-border bg-tcs-surface px-4 py-2 text-sm font-semibold text-tcs-text">
            <History className="h-4 w-4" /> Compensation history
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-tcs-border text-sm">
              <thead className="text-left text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
                <tr>
                  <th className="px-4 py-2">Effective</th>
                  <th className="px-4 py-2">Ended</th>
                  <th className="px-4 py-2">Rate</th>
                  <th className="px-4 py-2">Frequency</th>
                  <th className="px-4 py-2">Hours</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tcs-border">
                {history.data.map((h) => (
                  <tr key={h.comp_id}>
                    <td className="px-4 py-2 text-xs">{fmtMonthDayYear(h.effective_date)}</td>
                    <td className="px-4 py-2 text-xs">
                      {h.end_date ? fmtMonthDayYear(h.end_date) : 'current'}
                    </td>
                    <td className="px-4 py-2 text-xs">{formatPayRate(h, h.pay_type ?? payType)}</td>
                    <td className="px-4 py-2 text-xs">{h.pay_frequency ?? DASH}</td>
                    <td className="px-4 py-2 text-xs">{formatStandardHours(h)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {modal === 'edit' && (
        <EmployeeEditModal employee={e} onClose={() => setModal(null)} onSaved={refreshAll} />
      )}
      {modal === 'convert' && (
        <ConvertToClientModal
          employee={e}
          onClose={() => setModal(null)}
          onConverted={(fee) => {
            setModal(null);
            toast.success(
              fee > 0
                ? `Conversion recorded — $${fee.toLocaleString()} fee billed`
                : 'Conversion recorded — no fee (beyond 6 months)',
            );
            refreshAll();
          }}
        />
      )}
      {modal === 'comp' && employeeId && (
        <CompensationModal
          employeeId={employeeId}
          current={current}
          payType={payType}
          onClose={() => setModal(null)}
          onSaved={refreshAll}
        />
      )}
      {modal === 'benefit' && employeeId && (
        <BenefitElectionModal
          employeeId={employeeId}
          payType={payType}
          onClose={() => setModal(null)}
          onSaved={refreshAll}
        />
      )}
      {modal === 'paytype' && employeeId && (
        <PayTypeModal
          employeeId={employeeId}
          payType={payType}
          workState={s('work_state')}
          onClose={() => setModal(null)}
          onSaved={refreshAll}
        />
      )}
    </div>
  );
}

function Banner({ tone, children }: { tone: 'warn'; children: React.ReactNode }) {
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
        tone === 'warn'
          ? 'border-st-warn-border bg-st-warn-bg text-st-warn-fg-strong'
          : 'border-tcs-border bg-tcs-surface text-tcs-text-secondary'
      }`}
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const ok = status === 'Active';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs ${
        ok ? 'bg-st-ok-bg text-st-ok-fg-strong' : 'bg-tcs-border text-tcs-text-secondary'
      }`}
    >
      {status}
    </span>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
        {icon}
        <span>{title}</span>
      </div>
      <div className="mt-3 space-y-1.5">{children}</div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-5 items-start gap-2 text-sm">
      <span className="col-span-2 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      <span className="col-span-3 text-tcs-text">{value}</span>
    </div>
  );
}

/**
 * Benefits, status only.
 *
 * Hourly gets the "not eligible" copy the mockup asked for — but that is a
 * SENTENCE, not a rule. Elections that exist are still listed underneath, so an
 * hourly employee who was in fact enrolled shows up rather than being hidden by
 * a policy the UI decided to enforce on its own (the dispatch's "do not encode
 * 'hourly are never benefits-eligible' as a constraint" applies to the screen
 * too — a screen that refuses to display a row is a constraint with extra steps).
 */
function BenefitsSection({
  payType,
  rows,
  loading,
}: {
  payType: string | null;
  rows: Array<{
    enrollment_id: string;
    enrollment_status: string | null;
    plan_name?: string | null;
    plan_type?: string | null;
  }>;
  loading: boolean;
}) {
  const hourly = payType === 'Hourly';
  return (
    <Section icon={<ShieldCheck className="h-4 w-4" />} title="Benefits (status only)">
      {loading ? (
        <Skeleton className="h-12" />
      ) : (
        <>
          {hourly && rows.length === 0 && (
            <div className="space-y-1.5 py-1">
              <span className="inline-block rounded-full bg-tcs-border px-2 py-0.5 text-xs text-tcs-text-secondary">
                Not eligible
              </span>
              <p className="text-xs text-tcs-text-secondary">
                Hourly-paid employees don&rsquo;t receive health or 401(k) benefits.
              </p>
            </div>
          )}
          {hourly && rows.length > 0 && (
            <p className="pb-1 text-xs text-st-warn-fg-strong">
              Hourly-paid employees aren&rsquo;t normally eligible, but {rows.length} election
              {rows.length === 1 ? ' is' : 's are'} on file.
            </p>
          )}
          {!hourly && rows.length === 0 && (
            <p className="text-xs text-tcs-text-secondary">
              No elections on file{payType ? '' : ' (pay type is not set either)'}.
            </p>
          )}
          {rows.map((b) => (
            <Row
              key={b.enrollment_id}
              label={b.plan_name ?? b.plan_type ?? 'Plan'}
              value={<EnrollmentChip status={b.enrollment_status} />}
            />
          ))}
        </>
      )}
    </Section>
  );
}

const ENROLLMENT_TONE: Record<string, string> = {
  elected: 'bg-st-ok-bg text-st-ok-fg-strong',
  pending: 'bg-st-warn-bg text-st-warn-fg-strong',
  waived: 'bg-tcs-border text-tcs-text-secondary',
  not_eligible: 'bg-tcs-border text-tcs-text-secondary',
};

const ENROLLMENT_LABEL: Record<string, string> = {
  elected: 'Elected',
  pending: 'Pending',
  waived: 'Waived',
  not_eligible: 'Not eligible',
};

function EnrollmentChip({ status }: { status: string | null }) {
  const key = (status ?? '').trim();
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs ${
        ENROLLMENT_TONE[key] ?? 'bg-tcs-border text-tcs-text-secondary'
      }`}
    >
      {ENROLLMENT_LABEL[key] ?? (key || 'Unknown')}
    </span>
  );
}
